import { z } from "zod";
import { FILTER_LANGUAGE_ALL, FILTER_MODEL_GROUP_NONE } from "./SearchPage.hooks";
import { i18nConfig, Language } from "@/config";
import { sortOptionValues } from "@/@types/search";
import { ModelGroups, Languages, Categories } from "@/atoms/form";

export const searchFormModel = z.object({
  keyphrase: z.string().trim().optional(),
  filter: z
    .object({
      modelGroup: z
        .string()
        .transform((value) => {
          if (value === FILTER_MODEL_GROUP_NONE) {
            return undefined;
          }
          return value;
        })
        .optional(),
      modelCode: z
        .string()
        .transform((value) => {
          const trimmedValue = value.trim();
          return trimmedValue === "" ? undefined : trimmedValue;
        })
        .optional(),
      modelName: z
        .string()
        .transform((value) => {
          const trimmedValue = value.trim();
          return trimmedValue === "" ? undefined : trimmedValue;
        })
        .optional(),
      language: z
        .string()
        .transform((value) => {
          if (value === FILTER_LANGUAGE_ALL) {
            return undefined;
          }
          return value;
        })
        .optional(),
      categories: z
        .array(z.string())
        .transform((values) => {
          if (values.length === 0) {
            return undefined;
          }
          return values;
        })
        .optional(),
      onlyLatestVersion: z.boolean().default(true),
    })
    .transform((value) => {
      if (Object.values(value).every((value) => value === undefined)) {
        return undefined;
      }
      return value;
    })
    .optional(),
  sort: z.enum(sortOptionValues),
});

export type SearchFormModel = z.infer<typeof searchFormModel>;
export type SearchFilter = z.infer<typeof searchFormModel.shape.filter>;

export const toFormLanguageSelectBox = (language: Language) => {
  if (i18nConfig.locales.includes(language)) return language;
  return "en-US";
};

export const toSearchFormModelGroupsFilter = (modelGroups: ModelGroups) => {
  const defaultOption = { value: FILTER_MODEL_GROUP_NONE, label: FILTER_MODEL_GROUP_NONE };
  if (modelGroups.length === 0) return [defaultOption];
  return [
    defaultOption,
    ...modelGroups.map((modelGroup) => {
      return { value: modelGroup.code, label: modelGroup.name };
    }),
  ];
};

export const toSearchFormLanguagesFilter = (languages: Languages) => {
  const defaultOption = { value: FILTER_LANGUAGE_ALL, label: FILTER_LANGUAGE_ALL };
  if (languages.length === 0) return [defaultOption];
  return [
    defaultOption,
    ...languages.map((language) => {
      return { value: language.code, label: language.name };
    }),
  ];
};

export const toSearchFormFilters = (context: {
  modelGroups: ModelGroups;
  languages: Languages;
  categories: Categories;
}) => {
  const filterModelGroups = toSearchFormModelGroupsFilter(context.modelGroups);
  const filterLanguages = toSearchFormLanguagesFilter(context.languages);
  const filterCategories = context.categories.map((category) => {
    return { value: category.code, label: category.name };
  });
  return {
    filterModelGroups,
    filterLanguages,
    filterCategories,
  };
};
